import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Pencil, Trash2, ChevronLeft, ChevronRight, FileEdit, X } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { useEventTracking } from '@/hooks/useEventTracking';
import { formatDistanceToNow } from 'date-fns';
import type { Announcement, TemplateType } from '@/types/announcement';

const copy = {
  en: { title: 'Quiet Drafts', subtitle: 'Some messages take a little more time.', continueWriting: 'Continue Writing', deleteDraft: 'Delete', emptyTitle: 'No quiet drafts right now', emptyBody: 'When you start writing a message and need time to finish, it will wait for you here.', writeNew: 'Write a New Message', to: 'To', lastEdited: 'Last edited', saved: 'Saved' },
  es: { title: 'Borradores Silenciosos', subtitle: 'Algunos mensajes necesitan más tiempo.', continueWriting: 'Continuar Escribiendo', deleteDraft: 'Eliminar', emptyTitle: 'No hay borradores ahora', emptyBody: 'Cuando empieces a escribir un mensaje y necesites tiempo para terminarlo, te esperará aquí.', writeNew: 'Escribe un Nuevo Mensaje', to: 'Para', lastEdited: 'Última edición', saved: 'Guardado' },
  fr: { title: 'Brouillons Tranquilles', subtitle: 'Certains messages prennent un peu plus de temps.', continueWriting: 'Continuer à Écrire', deleteDraft: 'Supprimer', emptyTitle: 'Pas de brouillons pour le moment', emptyBody: 'Quand vous commencez un message et avez besoin de temps, il vous attendra ici.', writeNew: 'Écrire un Nouveau Message', to: 'À', lastEdited: 'Dernière édition', saved: 'Enregistré' },
  pt: { title: 'Rascunhos Quietos', subtitle: 'Algumas mensagens precisam de mais tempo.', continueWriting: 'Continuar Escrevendo', deleteDraft: 'Excluir', emptyTitle: 'Nenhum rascunho no momento', emptyBody: 'Quando você começar a escrever e precisar de tempo, a mensagem esperará por você aqui.', writeNew: 'Escreva uma Nova Mensagem', to: 'Para', lastEdited: 'Última edição', saved: 'Salvo' },
  ar: { title: 'مسودات هادئة', subtitle: 'بعض الرسائل تحتاج مزيدًا من الوقت.', continueWriting: 'تابع الكتابة', deleteDraft: 'حذف', emptyTitle: 'لا توجد مسودات حاليًا', emptyBody: 'عندما تبدأ كتابة رسالة وتحتاج وقتًا لإنهائها، ستنتظرك هنا.', writeNew: 'اكتب رسالة جديدة', to: 'إلى', lastEdited: 'آخر تعديل', saved: 'محفوظ' },
  ja: { title: '静かな下書き', subtitle: '言葉にするまで時間が必要なこともあります。', continueWriting: '書き続ける', deleteDraft: '削除', emptyTitle: '下書きはありません', emptyBody: 'メッセージを書き始めて時間が必要なとき、ここで待っています。', writeNew: '新しいメッセージを書く', to: '宛先', lastEdited: '最終編集', saved: '保存済み' },
  ko: { title: '조용한 초안', subtitle: '어떤 말은 좀 더 시간이 필요합니다.', continueWriting: '이어 쓰기', deleteDraft: '삭제', emptyTitle: '지금은 초안이 없어요', emptyBody: '메시지를 쓰다가 시간이 필요할 때, 여기서 기다리고 있을게요.', writeNew: '새 메시지 쓰기', to: '받는 사람', lastEdited: '마지막 수정', saved: '저장됨' },
  id: { title: 'Draf Tenang', subtitle: 'Beberapa pesan butuh waktu lebih.', continueWriting: 'Lanjut Menulis', deleteDraft: 'Hapus', emptyTitle: 'Belum ada draf saat ini', emptyBody: 'Saat Anda mulai menulis dan butuh waktu untuk menyelesaikan, pesan akan menunggu di sini.', writeNew: 'Tulis Pesan Baru', to: 'Untuk', lastEdited: 'Terakhir diedit', saved: 'Tersimpan' },

  hi: { title: 'शांत ड्राफ्ट', subtitle: 'कुछ संदेशों में थोड़ा अधिक समय लगता है।', continueWriting: 'लिखना जारी रखें', deleteDraft: 'हटाएँ', emptyTitle: 'अभी कोई शांत ड्राफ्ट नहीं', emptyBody: 'जब आप कोई संदेश लिखना शुरू करते हैं और उसे पूरा करने के लिए समय चाहिए होता है, तो वह यहां आपका इंतजार करेगा।', writeNew: 'एक नया संदेश लिखें', to: 'प्रति', lastEdited: 'अंतिम बार संपादित', saved: 'सहेजा गया' },
};

const INTENT_LABELS: Record<TemplateType, Record<string, string>> = {
  apology: { en: 'Apology', es: 'Disculpa', fr: 'Excuses', pt: 'Desculpa', ar: 'اعتذار', ja: '謝罪', ko: '사과', id: 'Permintaan Maaf' },
  gratitude: { en: 'Gratitude', es: 'Gratitud', fr: 'Gratitude', pt: 'Gratidão', ar: 'امتنان', ja: '感謝', ko: '감사', id: 'Rasa Syukur' },
  hard_conversation: { en: 'Hard Conversation', es: 'Conversación Difícil', fr: 'Conversation Difficile', pt: 'Conversa Difícil', ar: 'محادثة صعبة', ja: '難しい会話', ko: '어려운 대화', id: 'Percakapan Sulit' },
  love: { en: 'Love', es: 'Amor', fr: 'Amour', pt: 'Amor', ar: 'حب', ja: '愛', ko: '사랑', id: 'Cinta' },
  closure: { en: 'Closure', es: 'Cierre', fr: 'Clôture', pt: 'Encerramento', ar: 'إغلاق', ja: '終結', ko: '마무리', id: 'Penutupan' },
  custom: { en: 'Custom', es: 'Personalizado', fr: 'Personnalisé', pt: 'Personalizado', ar: 'مخصص', ja: 'カスタム', ko: '커스텀', id: 'Kustom' },
  birthday: { en: 'Birthday', es: 'Cumpleaños', fr: 'Anniversaire', pt: 'Aniversário', ar: 'عيد ميلاد', ja: '誕生日', ko: '생일', id: 'Ulang Tahun' },
  anniversary: { en: 'Anniversary', es: 'Aniversario', fr: 'Anniversaire de mariage', pt: 'Aniversário de casamento', ar: 'ذكرى سنوية', ja: '記念日', ko: '기념일', id: 'Hari Jadi' },
  celebration: { en: 'Celebration', es: 'Celebración', fr: 'Célébration', pt: 'Celebração', ar: 'احتفال', ja: 'お祝い', ko: '축하', id: 'Perayaan' },
  encouragement: { en: 'Encouragement', es: 'Ánimo', fr: 'Encouragement', pt: 'Encorajamento', ar: 'تشجيع', ja: '励まし', ko: '격려', id: 'Semangat' },
};

interface QuietDraftShelfProps {
  drafts: Announcement[];
  onDelete: (announcement: Announcement) => void;
}

export function QuietDraftShelf({ drafts, onDelete }: QuietDraftShelfProps) {
  const { language } = useLanguage();
  const { trackEvent } = useEventTracking();
  const scrollRef = useRef<HTMLDivElement>(null);
  const c = lc(copy, language);

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
  };

  // Track view
  const hasTracked = useRef(false);
  if (!hasTracked.current && drafts.length > 0) {
    hasTracked.current = true;
    trackEvent('quiet_drafts_viewed', { draft_count: drafts.length });
  }

  return (
    <section id="quiet-drafts" data-tour="quiet-drafts" className="mb-8">
      {/* Section Header */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <FileEdit className="h-5 w-5 text-primary/70" />
          <h2 className="text-xl font-semibold text-foreground tracking-tight">{c.title}</h2>
        </div>
        <p className="text-sm text-muted-foreground italic">{c.subtitle}</p>
      </div>

      {/* Shelf Container */}
      <Card className="rounded-2xl border-0 overflow-hidden relative"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--primary) / 0.04), hsl(var(--card)) 30%, hsl(var(--card)) 70%, hsl(var(--primary) / 0.06))',
          boxShadow: '0 4px 24px -6px hsl(var(--primary) / 0.08)',
        }}
      >
        {/* Emerald gradient edge */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

        <CardContent className="p-5">
          {drafts.length === 0 ? (
            /* Empty State */
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col items-center justify-center py-10 text-center"
            >
              <div className="w-14 h-14 rounded-full bg-primary/8 flex items-center justify-center mb-4">
                <FileEdit className="h-6 w-6 text-primary/50" />
              </div>
              <h3 className="text-base font-medium text-foreground mb-2">{c.emptyTitle}</h3>
              <p className="text-sm text-muted-foreground max-w-sm mb-5">{c.emptyBody}</p>
              <Link to="/create">
                <Button size="default" className="rounded-xl gap-2">
                  <Pencil className="h-4 w-4" />
                  {c.writeNew}
                </Button>
              </Link>
            </motion.div>
          ) : (
            /* Draft Cards */
            <div className="relative">
              {/* Scroll arrows — desktop only */}
              {drafts.length > 3 && (
                <>
                  <button
                    onClick={() => scroll('left')}
                    className="hidden md:flex absolute -left-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 items-center justify-center rounded-full bg-card shadow-elevation-2 border border-border/40 hover:bg-accent transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => scroll('right')}
                    className="hidden md:flex absolute -right-2 top-1/2 -translate-y-1/2 z-10 h-8 w-8 items-center justify-center rounded-full bg-card shadow-elevation-2 border border-border/40 hover:bg-accent transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </>
              )}

              <div
                ref={scrollRef}
                className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {drafts.map((draft, i) => (
                  <DraftCard
                    key={draft.id}
                    draft={draft}
                    index={i}
                    language={language}
                    labels={c}
                    onDelete={() => onDelete(draft)}
                    onContinue={() => trackEvent('quiet_draft_continue_clicked', { draft_id: draft.id })}
                  />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

/* ─── Draft nudge popup (fixed top) ──────────────────────── */

const nudgeCopy = {
  en: { msg: (n: number) => `You have ${n} unfinished message${n > 1 ? 's' : ''} waiting.`, continue: 'Continue Writing', dismiss: 'Dismiss' },
  es: { msg: (n: number) => `Tienes ${n} mensaje${n > 1 ? 's' : ''} sin terminar.`, continue: 'Continuar', dismiss: 'Descartar' },
  fr: { msg: (n: number) => `Vous avez ${n} message${n > 1 ? 's' : ''} en attente.`, continue: 'Continuer', dismiss: 'Ignorer' },
  pt: { msg: (n: number) => `Você tem ${n} mensagem(ns) esperando.`, continue: 'Continuar', dismiss: 'Ignorar' },
  ar: { msg: (n: number) => `لديك ${n} رسالة غير مكتملة.`, continue: 'تابع الكتابة', dismiss: 'تجاهل' },
  ja: { msg: (n: number) => `${n}件の未完成メッセージがあります。`, continue: '続ける', dismiss: '閉じる' },
  ko: { msg: (n: number) => `${n}개의 미완성 메시지가 있어요.`, continue: '이어 쓰기', dismiss: '닫기' },
  id: { msg: (n: number) => `Anda punya ${n} pesan belum selesai.`, continue: 'Lanjutkan', dismiss: 'Tutup' },
};

const DISMISSED_KEY = 'quiet_draft_nudge_dismissed';
const DISMISSED_SESSION_KEY = 'quiet_draft_nudge_session';

export function QuietDraftNudge({ draftCount, firstDraftId }: { draftCount: number; firstDraftId?: string }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(() => {
    // Reset dismissal on new auth session (sign-in clears the session marker)
    const currentSession = sessionStorage.getItem(DISMISSED_SESSION_KEY);
    if (!currentSession) {
      sessionStorage.removeItem(DISMISSED_KEY);
      sessionStorage.setItem(DISMISSED_SESSION_KEY, 'active');
    }
    return sessionStorage.getItem(DISMISSED_KEY) === 'true';
  });

  if (draftCount === 0 || dismissed) return null;

  const t = (nudgeCopy as Record<string, typeof nudgeCopy.en>)[language] || nudgeCopy.en;

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const handleContinue = () => {
    sessionStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
    if (firstDraftId) {
      navigate(`/edit/${firstDraftId}`);
    } else {
      document.getElementById('quiet-drafts')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -30 }}
      transition={{ type: 'spring', stiffness: 300, damping: 24 }}
      className="fixed top-16 sm:top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg"
    >
      <div
        className="relative flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 px-4 sm:px-5 py-3 sm:py-3.5 rounded-2xl border border-primary/20 shadow-lg backdrop-blur-md"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--card) / 0.95), hsl(var(--primary) / 0.08))',
          boxShadow: '0 8px 32px -8px hsl(var(--primary) / 0.2)',
        }}
      >
        {/* Dismiss X — absolute top-right on mobile */}
        <button
          onClick={handleDismiss}
          className="absolute top-2 right-2 sm:static h-7 w-7 sm:h-8 sm:w-8 flex items-center justify-center rounded-xl text-muted-foreground hover:text-foreground hover:bg-accent transition-colors order-last"
          aria-label={t.dismiss}
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start sm:items-center gap-2.5 pr-8 sm:pr-0 flex-1 min-w-0">
          <FileEdit className="h-5 w-5 text-primary shrink-0 mt-0.5 sm:mt-0" />
          <span className="text-sm font-medium text-foreground">{t.msg(draftCount)}</span>
        </div>

        <Button size="sm" className="rounded-xl text-xs h-8 px-4 gap-1.5 w-full sm:w-auto shrink-0" onClick={handleContinue}>
          <Pencil className="h-3 w-3" />
          {t.continue}
        </Button>
      </div>
    </motion.div>
  );
}

/* ─── Individual Draft Card ─────────────────────────────── */

interface DraftCardProps {
  draft: Announcement;
  index: number;
  language: string;
  labels: typeof copy['en'];
  onDelete: () => void;
  onContinue: () => void;
}

function DraftCard({ draft, index, language, labels, onDelete, onContinue }: DraftCardProps) {
  const intentLabel = (INTENT_LABELS[draft.template as TemplateType] as Record<string, string>)?.[language]
    || INTENT_LABELS[draft.template as TemplateType]?.en
    || draft.template;

  const timeAgo = formatDistanceToNow(new Date(draft.updated_at), { addSuffix: true });
  const preview = draft.message?.slice(0, 90) || '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.35 }}
      className="snap-start shrink-0 w-[260px] md:w-[280px]"
    >
      <div className="h-full rounded-xl border border-border/50 bg-card/80 backdrop-blur-sm p-4 flex flex-col gap-3 hover:shadow-elevation-2 transition-shadow duration-200">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {labels.to}: {draft.recipient_name}
            </p>
            <div className="flex items-center gap-1.5 mt-1">
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 font-medium">
                {intentLabel}
              </Badge>
            </div>
          </div>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 text-primary/70 border-primary/20 shrink-0">
            {labels.saved}
          </Badge>
        </div>

        {/* Message preview */}
        <p className="text-xs text-muted-foreground leading-relaxed line-clamp-3 flex-1">
          {preview || '...'}
        </p>

        {/* Timestamp */}
        <p className="text-[10px] text-muted-foreground/60">
          {labels.lastEdited} {timeAgo}
        </p>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1 border-t border-border/30">
          <Link
            to={`/edit/${draft.id}`}
            onClick={onContinue}
            className="flex-1"
          >
            <Button size="sm" className="w-full rounded-lg gap-1.5 text-xs h-8">
              <Pencil className="h-3 w-3" />
              {labels.continueWriting}
            </Button>
          </Link>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="h-8 w-8 flex items-center justify-center rounded-lg text-muted-foreground/50 hover:text-destructive hover:bg-destructive/8 transition-colors"
            aria-label={labels.deleteDraft}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
